import React, { useState, useEffect, useCallback } from 'react';
import MarketCard from '../components/MarketCard/MarketCard';
import BettingOverlay from '../components/MarketCard/betting/BettingOverlay';
import { useMarketDataNew } from '../hooks/useMarketDataNew';


function HomePage({ activeFilter, onFilterChange }) {
  // 使用市场数据Hook
  const { 
    marketData, 
    chartData,
    isConnected, 
    realtimeCount, 
    totalCount,
    getMarketsByTag,
    getWatchlistMarkets 
  } = useMarketDataNew();

  // 全局蒙版状态管理
  const [globalBettingOverlay, setGlobalBettingOverlay] = useState({
    show: false,
    symbol: '',
    area: '',
    lastPrice: 0
  });

  // 分页加载状态
  const [displayedMarkets, setDisplayedMarkets] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const ITEMS_PER_PAGE = 10;
  
  // 筛选状态（从Layout接收）
  const [filteredMarkets, setFilteredMarkets] = useState([]);
  
  // 全局蒙版处理函数
  const handleGlobalAreaClick = (symbol, area, lastPrice) => {
    setGlobalBettingOverlay({
      show: true,
      symbol,
      area,
      lastPrice
    });
  };
  
  const handleCloseGlobalOverlay = () => {
    setGlobalBettingOverlay({
      show: false,
      symbol: '',
      area: '',
      lastPrice: 0
    });
  };
  
  // 筛选处理函数
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    // 重置分页状态
    setDisplayedMarkets([]);
    setCurrentPage(0);
    setHasMore(true);
  };
  
  // 根据筛选条件过滤数据
  const getFilteredMarkets = useCallback(() => {
    let filtered;
    switch (activeFilter) {
      case 'Active':
        filtered = marketData; // 默认显示所有
        break;
      case 'Watchlist':
        filtered = getWatchlistMarkets();
        break;
      case 'Major':
      case 'New':
      case 'Altcoins':
      case 'Meme':
      case 'RWA':
      case 'Payments':
      case 'AI':
      case 'Metaverse':
      case 'Seed':
      case 'DeFi':
        filtered = getMarketsByTag(activeFilter);
        break;
      default:
        filtered = marketData;
    }
    console.log(`筛选结果 [${activeFilter}]:`, filtered.length, `个市场 (实时数据: ${realtimeCount}/${totalCount})`);
    return filtered;
  }, [activeFilter, marketData, getMarketsByTag, getWatchlistMarkets, realtimeCount, totalCount]);

  // 加载更多数据
  const loadMoreMarkets = useCallback(() => {
    if (loading || !hasMore) {
      console.log('加载被阻止:', { loading, hasMore });
      return;
    }
    
    // console.log('开始加载更多数据, 当前页:', currentPage);
    setLoading(true);
    
    // 模拟异步加载
    setTimeout(() => {
      const filteredData = getFilteredMarkets();
      const startIndex = currentPage * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      const newMarkets = filteredData.slice(startIndex, endIndex);
      
      if (newMarkets.length > 0) {
        setDisplayedMarkets(prev => [...prev, ...newMarkets]);
        setCurrentPage(prev => prev + 1);
        
        // 检查是否还有更多数据
        if (endIndex >= filteredData.length) {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
      
      setLoading(false);
    }, 500); // 模拟网络延迟
  }, [currentPage, loading, hasMore, getFilteredMarkets]);

  // 监听筛选条件变化，直接更新显示的数据
  useEffect(() => {
    // console.log('筛选条件变化或初始化:', activeFilter);
    
    // 直接在useEffect内部实现筛选逻辑
    let filteredData;
    switch (activeFilter) {
      case 'Active':
        filteredData = marketData; // 默认显示所有
        break;
      case 'Watchlist':
        filteredData = getWatchlistMarkets();
        break;
      case 'Major':
      case 'New':
      case 'Altcoins':
      case 'Meme':
      case 'RWA':
      case 'Payments':
      case 'AI':
      case 'Metaverse':
      case 'Seed':
      case 'DeFi':
        filteredData = getMarketsByTag(activeFilter);
        break;
      default:
        filteredData = marketData;
    }
    
    const firstPageData = filteredData.slice(0, ITEMS_PER_PAGE);
    
    // console.log('筛选后数据:', filteredData.length, '个，首页显示:', firstPageData.length, '个');
    
    // 更新显示的数据
    setDisplayedMarkets(firstPageData);
    setCurrentPage(1); // 已加载第一页
    setHasMore(filteredData.length > ITEMS_PER_PAGE);
    setLoading(false);
    
    // 更新filteredMarkets状态供无限滚动使用
    setFilteredMarkets(filteredData);
  }, [activeFilter, marketData, getMarketsByTag, getWatchlistMarkets]);

  // 滚动监听，实现无限滚动
  useEffect(() => {
    const mainElement = document.querySelector('main');
    if (!mainElement) return;
    
    const handleScroll = () => {
      if (loading || !hasMore) return;
      
      const scrollTop = mainElement.scrollTop;
      const scrollHeight = mainElement.scrollHeight;
      const clientHeight = mainElement.clientHeight;
      
      // 当滚动到距离底部200px时开始加载
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        // 直接调用加载逻辑，避免依赖问题
        if (!loading && hasMore) {
          setLoading(true);
          setTimeout(() => {
            setCurrentPage(prevPage => {
              const filteredData = getFilteredMarkets();
              const startIndex = prevPage * ITEMS_PER_PAGE;
              const endIndex = startIndex + ITEMS_PER_PAGE;
              const newMarkets = filteredData.slice(startIndex, endIndex);
              
              if (newMarkets.length > 0) {
                setDisplayedMarkets(prev => [...prev, ...newMarkets]);
                
                // 检查是否还有更多数据
                if (endIndex >= filteredData.length) {
                  setHasMore(false);
                }
                return prevPage + 1;
              } else {
                setHasMore(false);
                return prevPage;
              }
            });
            setLoading(false);
          }, 500);
        }
      }
    };

    mainElement.addEventListener('scroll', handleScroll);
    return () => mainElement.removeEventListener('scroll', handleScroll);
  }, [loading, hasMore, ITEMS_PER_PAGE]);

  return (
    <div className="overflow-y-auto">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* WebSocket状态显示 */}
        <div className="mb-6">

        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
          {displayedMarkets.map((market, index) => (
            <MarketCard
              key={`${market.coinName}-${index}`}
              coinName={market.coinName}
              currentPrice={market.currentPrice}
              priceChange={market.priceChange}
              watchlistCount={market.watchlistCount}
              participantCount={market.participantCount}
              isWatchlisted={market.isWatchlisted}
              predictions={market.predictions}
              totalVolume={market.totalVolume}
              trend={market.trend}
              tags={market.tags}
              chartData={chartData[market.symbol] || []}
              globalBettingOverlay={globalBettingOverlay}
              onGlobalAreaClick={handleGlobalAreaClick}
              onCloseGlobalOverlay={handleCloseGlobalOverlay}
            />
          ))}
        </div>
        
        {/* 加载指示器 */}
        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="flex items-center space-x-2 text-white/60">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white/60"></div>
              <span className="text-sm">Loading more crypto coins...</span>
            </div>
          </div>
        )}
        
        {/* 没有更多数据时的提示 */}
        {!hasMore && displayedMarkets.length > 0 && (
          <div className="text-center py-8 text-white/60">
            <p className="text-sm">You've reached the end of the list</p>
          </div>
        )}
        
        {/* 没有数据时的提示 */}
        {!loading && displayedMarkets.length === 0 && (
          <div className="text-center py-16 text-white/60">
            <p className="text-lg mb-2">No markets found</p>
            <p className="text-sm">Try adjusting your filters</p>
          </div>
        )}
      </div>
      
      {/* 全局下注蒙版 */}
      {globalBettingOverlay.show && (
        <BettingOverlay
          symbol={globalBettingOverlay.symbol}
          area={globalBettingOverlay.area}
          lastPrice={globalBettingOverlay.lastPrice}
          onClose={handleCloseGlobalOverlay}
        />
      )}
      

    </div>
  );
}

export default HomePage;
